import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/api/client';
import { setSession } from '@/auth/session';
import { Button } from '@/design/components/Button';
import { Dialog } from '@/design/components/Dialog';
import { Field, TextInput } from '@/design/components/Field';
import { Banner } from '@/design/components/Display';
import { errorMessage } from '@/i18n/errors';

const bytes = (s: string) => new TextEncoder().encode(s).length;

export function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const change = useMutation({
    mutationFn: () => api('/auth/password', { body: { currentPassword: current, newPassword: next } }),
    // Changing the password closes every session, including this one.
    onSuccess: () => setSession(null),
  });
  const tooShort = next.length > 0 && next.length < 12;
  const tooLong = bytes(next) > 72;
  const mismatch = repeat.length > 0 && repeat !== next;
  const valid = current && next.length >= 12 && !tooLong && next === repeat;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Cambiar contraseña"
      description="Al cambiarla se cierran todas las sesiones abiertas y vas a tener que ingresar de nuevo."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button variant="gold" disabled={!valid} loading={change.isPending} onClick={() => change.mutate()}>
            Cambiar contraseña
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {change.isError && <Banner tone="danger">{errorMessage(change.error)}</Banner>}
        <Field label="Contraseña actual">
          {(p) => <TextInput {...p} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}
        </Field>
        <Field
          label="Nueva contraseña"
          hint="Al menos 12 caracteres. Una frase fácil de recordar funciona muy bien."
          error={tooShort ? 'Tiene que tener al menos 12 caracteres.' : tooLong ? 'Es demasiado larga.' : null}
        >
          {(p) => <TextInput {...p} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />}
        </Field>
        <Field label="Repetí la nueva contraseña" error={mismatch ? 'No coincide.' : null}>
          {(p) => <TextInput {...p} type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}
